import { getWorkerDisplayLabel } from "../../../../utils/business/worker";

/**
 * Callout when the proposed crew differs from the reference assignment (block default vs. aspect, or edits in progress).
 * Two columns when both differ: added (left), removed (right). Single column when only one side differs.
 *
 * @param {Object} props
 * @param {Array<Object>} props.removed On block plan, not on this aspect day
 * @param {Array<Object>} props.added On this aspect day, not on block plan
 * @param {string} [props.heading] Optional title above the columns
 * @param {'override'|'neutral'} [props.tone='override'] — `override`: orange (daily vs block); `neutral`: gray (editing default crew vs current)
 * @returns {JSX.Element|null}
 */
export default function WorkerAssignmentDiffBanner({
  removed,
  added,
  heading,
  tone = "override",
}) {
  const r = removed ?? [];
  const a = added ?? [];
  if (r.length === 0 && a.length === 0) {
    return null;
  }

  const showAdded = a.length > 0;
  const showRemoved = r.length > 0;
  const twoCols = showAdded && showRemoved;

  const shellClass =
    tone === "neutral"
      ? "rounded-md border border-gray-200 bg-gray-100/90 px-2 py-1"
      : "rounded-md border border-orange-300 bg-orange-50/90 px-2 py-1";
  const headingClass =
    tone === "neutral"
      ? "text-sm font-semibold text-gray-800"
      : "text-sm font-semibold text-orange-800";

  return (
    <div className={shellClass} role="status">
      <div className="flex gap-0">
        <div className="min-w-0 flex-1">
          {heading ? (
            <p className={headingClass}>{heading}</p>
          ) : null}
          <div
            className={
              twoCols
                ? `grid grid-cols-2 gap-2`
                : heading
                  ? "mt-1"
                  : ""
            }
          >
            {showAdded ? (
              <div className="min-w-0 -mt-1">
                <ul className="mt-1 space-y-0 text-sm">
                  {a.map((w) => (
                    <li key={`added-${w.id}`} className="text-emerald-800">
                      + {getWorkerDisplayLabel(w)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {showRemoved ? (
              <div className="min-w-0 -mt-1">
                <ul className="mt-1 space-y-0 text-sm">
                  {r.map((w) => (
                    <li key={`removed-${w.id}`} className="text-red-600">
                      - {getWorkerDisplayLabel(w)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
